import { api } from "./base_service";
import { apiEndpoints } from "@/src/config/api/endpoints";
import { adaptMatch, normalizeInputDob, type SearchResultRaw } from "./screening_service";
import { normalizeDelimitedList } from "./normalize_helpers";
import { selectionIdForApiToken } from "@/components/watchlists";
import { ADVERSE_MEDIA_KEYWORD_LABELS } from "./search_configurations_service";
import { isoCountryName } from "@/app/(app)/_lib/iso-countries";
import type { ApiError, Paginated, Result } from "./types";
import type { ScreeningRun } from "@/src/pages/dashboard/_data";
import type { Match, RiskLevel, SourceId } from "@/app/(app)/screening/_data/types";

export const RECENT_SEARCHES_PAGE_SIZE = 10;

type RecentSearchRaw = Record<string, unknown>;

function str(v: unknown): string {
  if (typeof v === "string") return v;
  if (v == null) return "";
  return String(v);
}

function num(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function firstIso(v: unknown): string | null {
  const codes = normalizeDelimitedList(v);
  return codes[0] ?? null;
}

function riskFromScore(score: number): RiskLevel {
  if (score >= 0.9) return "critical";
  if (score >= 0.8) return "high";
  if (score >= 0.6) return "medium";
  return "low";
}

function adaptRecentResult(
  raw: Record<string, unknown>,
  searchId: string | undefined,
  rowTaggedAdverseMedia: boolean,
): Match {
  const rawDataSource = raw.data_source;
  const isMediaTag =
    typeof rawDataSource === "string" && rawDataSource.toUpperCase() === "ADV-MEDIA";
  const looksLikeArticle = typeof raw.title === "string" && raw.name == null;
  const isAdverseMedia =
    str(raw.entity_type) === "Media" || isMediaTag || (rowTaggedAdverseMedia && looksLikeArticle);

  if (!isAdverseMedia) {
    return adaptMatch(raw as unknown as SearchResultRaw, searchId);
  }

  const countryIso = str(
    raw.country ??
      (Array.isArray(raw.country_residence) ? raw.country_residence[0] : undefined) ??
      raw.nationality,
  );
  const country = countryIso ? isoCountryName(countryIso) || countryIso : "";
  const fullTitle = str(raw.title ?? raw.name);
  const url = str(raw.url);
  const mediaSource =
    raw.media_source && typeof raw.media_source === "object"
      ? (raw.media_source as Record<string, unknown>)
      : null;
  const mediaSourceName = str(mediaSource?.name ?? raw.media_source_name);
  const mediaSourceUrl = str(mediaSource?.url ?? raw.media_source_url);
  const keywords = Array.isArray(raw.keywords)
    ? raw.keywords.filter((k): k is string => typeof k === "string")
    : [];
  const individuals = Array.isArray(raw.individuals)
    ? raw.individuals.filter((i): i is string => typeof i === "string")
    : [];
  const entities = Array.isArray(raw.entities)
    ? raw.entities.filter((e): e is string => typeof e === "string")
    : [];
  const language = str(raw.language);
  const image = Array.isArray(raw.image) && raw.image.length > 0 ? str(raw.image[0]) : "";
  const description = str(raw.description);
  const body = str(raw.body);
  const rawScore = num(raw.confidence_score ?? raw.score);
  const score = rawScore == null ? 0 : rawScore > 1 ? rawScore / 100 : rawScore;

  const additionalInformation: Record<string, string> = {};
  if (fullTitle.length > 100) additionalInformation["Full Title"] = fullTitle;
  if (url) additionalInformation["Article URL"] = url;
  if (mediaSourceName) additionalInformation["Media Source"] = mediaSourceName;
  if (mediaSourceUrl) additionalInformation["Source URL"] = mediaSourceUrl;
  if (keywords.length) {
    additionalInformation["Keywords"] = keywords
      .map((k) => ADVERSE_MEDIA_KEYWORD_LABELS[k] ?? k)
      .join(", ");
  }
  if (individuals.length) additionalInformation["Individuals"] = individuals.join(", ");
  if (entities.length) additionalInformation["Entities"] = entities.join(", ");
  if (language) additionalInformation["Language"] = language;
  if (image) additionalInformation["Image"] = image;

  return {
    id: str(raw.si_identifier) || url || fullTitle,
    score,
    name: fullTitle.length > 100 ? `${fullTitle.slice(0, 100)}...` : fullTitle,
    type: "Media",
    country,
    countryIso: countryIso || undefined,
    dob: null,
    source: "ADV-MEDIA",
    collection: "adverse_media",
    lastUpdated: str(raw.published_at ?? raw.last_update).slice(0, 10),
    riskLevel: riskFromScore(score),
    alternativeNames: [],
    knownLocations: country ? [{ label: "Country", value: country }] : [],
    remarks: description ? [description] : [],
    additionalInformation,
    articleBody: body || undefined,
    searchId,
  };
}

export function timeAgo(iso: string, now: number = Date.now()): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const s = Math.max(0, Math.floor((now - t) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? "yesterday" : `${d}d ago`;
}

export function adaptRecentSearch(
  raw: RecentSearchRaw,
  now: number = Date.now(),
  index = 0,
): ScreeningRun {
  const search = (raw.search ?? {}) as Record<string, unknown>;
  const params = (search.params ?? {}) as Record<string, unknown>;
  const searchDataSourceTag = str(search.data_source).toUpperCase();

  const runAt = str(search.timestamp ?? raw.timestamp ?? raw.created_at ?? raw.run_at);
  const query = str(
    params.name ?? params.query ?? raw.query ?? raw.name ?? raw.search_term ?? raw.term ?? raw.q,
  );

  const rawScore = num(
    params.min_score ??
      params.score ??
      raw.min_score ??
      raw.confidence_score ??
      raw.score ??
      raw.threshold,
  );
  const minScore = rawScore == null ? 0 : rawScore > 1 ? rawScore / 100 : rawScore;

  const rawSource = params.data_source ?? search.data_source ?? raw.data_source ?? raw.source;

  const normalizedSourceIds = normalizeDelimitedList(rawSource).map((s) => s.toUpperCase());
  const sourceIds = normalizedSourceIds.length > 0 ? normalizedSourceIds : ["SANCTIONS-LISTS"];
  const source = sourceIds[0];

  const identifier = str(params.identifier ?? raw.identifier ?? "");

  const rawCountry =
    params.country ?? params.countries ?? raw.country ?? raw.countries ?? raw.country_code;
  const countryIso = firstIso(rawCountry);
  const countries = normalizeDelimitedList(rawCountry);

  const hits = num(raw.count ?? raw.hits ?? raw.match_count ?? raw.total_matches);

  const rawEntityType = str(params.entity_type ?? raw.entity_type ?? "").toLowerCase();
  const entityType =
    rawEntityType === "entity" ||
    rawEntityType === "organisation" ||
    rawEntityType === "organization"
      ? "Organization"
      : rawEntityType === "individual"
        ? "Individual"
        : rawEntityType === "vessel"
          ? "Vessel"
          : rawEntityType === "aircraft"
            ? "Aircraft"
            : rawEntityType === "country"
              ? "Country"
              : null;

  const rawDob = (params.date_of_birth ?? params.dob ?? raw.date_of_birth ?? raw.dob) as
    | string
    | string[]
    | undefined;
  const dob = normalizeInputDob(rawDob);

  const rawDataSource = str(params.data_source ?? search.data_source ?? raw.data_source ?? "");
  const watchlists = rawDataSource
    ? rawDataSource
        .split(",")
        .map((t) => selectionIdForApiToken(t.trim().toUpperCase()))
        .filter((id) => id.length > 0)
    : undefined;

  const rawResults = Array.isArray(raw.results)
    ? (raw.results as Record<string, unknown>[])
    : undefined;

  const realSearchId = str(
    (search as Record<string, unknown>).id ?? raw.search_id ?? raw.uuid ?? raw.id ?? raw.pk,
  );
  const searchId = realSearchId || `recent-${index}`;

  return {
    id: searchId,
    searchId: realSearchId || undefined,
    query,
    identifier: identifier || undefined,
    countryIso,
    countries: countries.length > 0 ? countries : undefined,
    sourceIds,
    minScore,
    topSource: source as SourceId,
    hits: hits ?? 0,
    seen: runAt ? timeAgo(runAt, now) : "",
    runAt,
    entityType,
    dob: dob || undefined,
    watchlists,

    results: rawResults?.map((r) =>
      adaptRecentResult(r, realSearchId || undefined, searchDataSourceTag === "ADV-MEDIA"),
    ),
  };
}

export type RecentSearchesPage = { runs: ScreeningRun[]; count: number };

export async function getRecentSearches(
  page: number = 1,
  now: number = Date.now(),
): Promise<Result<RecentSearchesPage>> {
  try {
    const data = await api.get<Paginated<RecentSearchRaw> | RecentSearchRaw[]>(
      apiEndpoints.recentSearches().list(),
      { params: { page, page_size: RECENT_SEARCHES_PAGE_SIZE } },
    );
    const rows = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
    const count = Array.isArray(data) ? rows.length : (data?.count ?? rows.length);
    return { ok: true, value: { runs: rows.map((r, i) => adaptRecentSearch(r, now, i)), count } };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}
